import {
    ArrayInput,
    AutocompleteInput,
    Breadcrumb,
    Create,
    DateInput,
    NumberInput,
    ReferenceInput,
    SelectInput,
    SimpleForm,
    SimpleFormIterator,
    TextInput,
} from '@/components/admin';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileDown, FileText, Landmark, Truck, WalletCards } from "lucide-react";
import { required, useGetList, useNotify, useRedirect } from 'ra-core';
import { useLocation, Link } from 'react-router-dom';
import { supabase } from '../../providers/supabase/supabase';
import { AutoPopulateVendorDetails } from '../shared/AutoPopulateVendorDetails';
import { AutoReferenceField } from '../shared/AutoReferenceField';
import { DocumentItemsTable } from '../shared/DocumentItemsTable';
import { GrandTotalsWatcher } from '../shared/GrandTotalsWatcher';
import { PaymentSplitWatcher } from '../shared/PaymentSplitWatcher';
import { TermsTemplateSelector } from '../shared/TermsTemplateSelector';
import { INCOTERM_CHOICES } from '../shared/companyConstants';
import { requireNonZeroTotal } from '../shared/validators';

const PAYMENT_TERM_CHOICES = [
    { id: '07 - 10 Days', name: '07 - 10 Days' },
    { id: '15 - 20 Days', name: '15 - 20 Days' },
    { id: '30 Days', name: '30 Days' },
    { id: 'Advance', name: 'Advance' },
    { id: 'Against Delivery', name: 'Against Delivery' },
    { id: 'LC', name: 'Letter of Credit' },
];

const POCreateTitle = () => (
    <span className="text-xl font-bold tracking-tight">Create Purchase Order</span>
);

export const POCreate = () => {
    const location = useLocation();
    const notify = useNotify();
    const redirect = useRedirect();
    const sourceRecord = location.state?.record || {};

    const { data: terms } = useGetList('terms_templates', {
        pagination: { page: 1, perPage: 1 },
        filter: { is_default: true }
    });
    
    const defaultTerms = terms && terms.length > 0 ? terms[0].content : '';

    const onSuccess = async (data: any) => {
        const SMALL_ORDER_LIMIT = 50000;
        const totalAmount = parseFloat(data.total_amount || '0');
        const isSmallOrder = totalAmount < SMALL_ORDER_LIMIT && totalAmount > 0;

        try {
            const { data: vendor } = await supabase.from('vendors').select('status').eq('id', data.vendor_id).single();
            
            if (vendor && vendor.status !== 'approved') {
                if (isSmallOrder) {
                    notify(`Small Order Bypass: PO for ${totalAmount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })} created without full vendor approval.`, { type: 'info', autoHideDuration: 10000 });
                } else {
                    await supabase.from('supplier_flags').insert({
                        vendor_id: data.vendor_id,
                        po_id: data.id,
                        reason: `PO for ${totalAmount.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })} created with unapproved vendor (Exceeds ₹50k threshold)`
                    });
                    notify('Unapproved supplier — this PO has been flagged for manager review (Threshold exceeded)', { type: 'warning', autoHideDuration: 10000 });
                }
            } else {
                notify('ra.notification.created', { type: 'info', messageArgs: { smart_count: 1 } });
            }
        } catch (error) {
            console.error('PO Success check error:', error);
            notify('ra.notification.created', { type: 'info', messageArgs: { smart_count: 1 } });
        }
        redirect(`/purchase_orders/${data.id}/show`);
    };

    return (
        <Create disableBreadcrumb 
            title={<POCreateTitle />}
            record={{ terms_conditions: defaultTerms, ...sourceRecord }} 
            mutationOptions={{ onSuccess }}
        >
            <Breadcrumb>
                <Breadcrumb.Item><Link to="/purchase_orders">Purchase Orders</Link></Breadcrumb.Item>
                <Breadcrumb.PageItem>Create</Breadcrumb.PageItem>
            </Breadcrumb>
            <SimpleForm className="max-w-5xl mx-auto space-y-6" validate={requireNonZeroTotal}>
                <AutoPopulateVendorDetails />
                <PaymentSplitWatcher />
                
                {/* General Details Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-muted/50">
                        <div className="flex items-center gap-2">
                            <FileDown className="w-5 h-5 text-foreground" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Purchase Order Details</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-6">
                        {/* Row 1: Reference, Date, VND Code */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <AutoReferenceField docType="purchase_order" />
                            <DateInput source="date" label="Date" defaultValue={new Date()} className="w-full" />
                            <TextInput source="vnd_code" label="VND Code" className="w-full" />
                        </div>

                        {/* Row 2: Vendor + Project + Bill To */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                            <ReferenceInput source="vendor_id" reference="vendors" filter={{ "status@in": "(approved,pending_approval,checked,conditional,quick_vendor)" }}>
                                <AutocompleteInput label="Supplier" optionText="name" validate={required()} className="w-full" />
                            </ReferenceInput>
                            <ReferenceInput source="sales_order_id" reference="sales_orders">
                                <AutocompleteInput label="Linked SO (opt)" optionText="reference" className="w-full" />
                            </ReferenceInput>
                            <ReferenceInput source="project_id" reference="projects">
                                <AutocompleteInput label="Linked Project (opt)" optionText="name" className="w-full" />
                            </ReferenceInput>
                            <ReferenceInput source="company_id" reference="companies">
                                <AutocompleteInput label="Ship To (Delivery Destination)" optionText="name" validate={required()} className="w-full" />
                            </ReferenceInput>
                        </div>

                        {/* Vendor contact override */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-dashed">
                            <TextInput source="vendor_contact_person" label="Vendor Contact Person" className="w-full" />
                            <TextInput source="vendor_contact_no" label="Vendor Contact No." className="w-full" />
                        </div>
                    </CardContent>
                </Card>

                {/* Shipping / Logistics Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <Truck className="w-5 h-5 text-muted-foreground" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Shipping & Logistics</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            <TextInput source="shipping_via" label="Shipping Via" className="w-full" />
                            <TextInput source="shipping_method" label="Shipping Method" className="w-full" />
                            <DateInput source="delivery_date" label="Delivery Date" className="w-full" />
                            <SelectInput source="delivery_term" label="Incoterms / Delivery Instruction" choices={INCOTERM_CHOICES} className="w-full" />
                            <TextInput source="freight_terms" label="FOR (Freight Terms)" className="w-full md:col-span-2" helperText="e.g. FOR Destination / FOR Site / Paid / To-Pay" />
                        </div>
                    </CardContent>
                </Card>

                {/* Line Items & Financials Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <Landmark className="w-5 h-5 text-primary" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Line Items & Financials</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-6">
                        <DocumentItemsTable showPercentage />
                        <GrandTotalsWatcher />

                        {/* Tax summary */}
                        <div className="bg-muted p-4 rounded-lg border space-y-4">
                            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                                Tax & Total Breakdown
                            </p>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                                <NumberInput source="cgst_amount" label="CGST (₹)" readOnly defaultValue={0} className="w-full" />
                                <NumberInput source="sgst_amount" label="SGST (₹)" readOnly defaultValue={0} className="w-full" />
                                <NumberInput source="igst_amount" label="IGST (₹)" readOnly defaultValue={0} className="w-full" />
                                <NumberInput source="total_amount" label="Grand Total (₹)" readOnly validate={required()} className="w-full font-bold text-primary" />
                            </div>
                            <TextInput source="amount_in_words" label="Amount in Words" className="w-full italic" readOnly helperText="Auto-generated from Total" />
                        </div>
                    </CardContent>
                </Card>

                {/* Payment Terms Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b">
                        <div className="flex items-center gap-2">
                            <WalletCards className="w-5 h-5 text-emerald-500" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Payment Terms</CardTitle>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6">
                        <ArrayInput source="payment_terms" label="">
                            <SimpleFormIterator className="flex flex-col gap-2">
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end w-full">
                                    <SelectInput source="payment_term" label="Payment Term" choices={PAYMENT_TERM_CHOICES} helperText={false} className="w-full" />
                                    <TextInput source="description" label="Description" helperText={false} className="w-full" />
                                    <TextInput source="invoice_portion" label="Invoice Portion (%)" helperText="e.g. 10 or 10%" className="w-full" />
                                    <NumberInput source="payment_amount" label="Payment Amount (₹)" readOnly helperText="Auto = portion% × total" className="w-full" />
                                </div>
                            </SimpleFormIterator>
                        </ArrayInput>
                    </CardContent>
                </Card>

                {/* Terms and Notes Card */}
                <Card className="w-full shadow-sm">
                    <CardHeader className="pb-3 border-b bg-muted/50">
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center gap-2">
                                <FileText className="w-5 h-5 text-primary" />
                                <CardTitle className="text-lg font-semibold tracking-tight">Terms & Instructions</CardTitle>
                            </div>
                            <TermsTemplateSelector type="purchase_order" />
                        </div>
                    </CardHeader>
                    <CardContent className="pt-6 space-y-4">
                        <TextInput source="terms_conditions" label="Terms & Conditions" multiline rows={4} className="w-full max-w-none" />
                        <TextInput source="comments" label="Special Instructions / Comments" multiline rows={3} className="w-full max-w-none" />
                    </CardContent>
                </Card>

            </SimpleForm>
        </Create>
    );
};
